import { useEffect, useState } from "react";
import { Checkbox, ClearableInput, Select, SimpleTooltip, Spinner } from "@rio-cloud/rio-uikit";
import { Asset, AssetGroup } from "../../models/asset";

import UptimeTagRow from "./components/UptimeTagRow";

import useUptimeSidebar from "../../hooks/useUptimeSidebar";
import VehicleIcon from "../VehicleIcon";

import "./styles.scss";

interface UptimeSidebarProps {
	allTags: AssetGroup[];
	allAssets: Asset[];
	setAssetIds: (assetIds: string[]) => void;
}

const LIMIT = 50;

const UptimeSidebar = ({
	allTags = [],
	allAssets = [],
	setAssetIds,
}: UptimeSidebarProps) => {

	const {
		tagWithAssets,
		getSearchLabel,
		viewMode,
		setViewMode,
		handleFilter,
		selectedTags,
		getVehicleTypeCount,
		searchQuery,
		addTag,
		removeTag,
		addAllTags,
		removeAllTags,
	} = useUptimeSidebar(allTags, allAssets);

	const [updateSnapshot, setUpdateSnapshot] = useState(0);
	const [page, setPage] = useState(1);
	
	useEffect(() => {
		if(tagWithAssets) {
			setAssetIds(Object.values(tagWithAssets).map((tag) => Array.from<string>(tag?.selectedAssetIds ?? new Set())).flat());
		}
	}, [tagWithAssets, updateSnapshot]);

	// Reset page when search query changes
	useEffect(() => {
		setPage(1);
	}, [searchQuery]);

	if (!allAssets?.length || !allTags?.length) {
		return (
			<div className="height-100pct display-flex align-items-center justify-content-center">
				<Spinner />
			</div>
		);
	}

	// Filter tags based on search query
	const filteredTags = allTags.filter((tag: AssetGroup) => {
		if (searchQuery && viewMode === "tags" && !tag.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
		return true;
	});

	// Apply pagination only if no search query is active
	const paginatedTags = searchQuery ? filteredTags : filteredTags.slice(0, page * LIMIT);

	return (
		<div className="Tree" style={{ height: "100%" }}>
			<div className="TreeHeader">
				<div className="TreeSearch width-100pct">
					<div className="input-group width-100pct">
						<div className="input-group width-100pct">
							<span className="input-group-addon">
								<span className="rioglyph rioglyph-search" aria-hidden="true" />
							</span>{" "}
							<ClearableInput placeholder={getSearchLabel(viewMode)} onChange={handleFilter} />
							<SimpleTooltip content={"Tipo de busca"}>
								<span className="input-group-btn">
									<Select
										pullRight={true}
										showSelectedItemIcon={true}
										dropdownClassName="width-auto"
										onChange={(item) => setViewMode(item?.short!)}
										options={[
											{
												id: "1",
												label: "Veículos",
												short: "assets",
												icon: <span className="rioglyph text-size-16 rioglyph-status-driving text-color-primary" />,
												selected: viewMode == "assets",
											},
											{
												id: "2",
												label: "Grupos",
												short: "tags",
												icon: <span className="rioglyph text-size-16 rioglyph-tag text-color-primary" />,
												selected: viewMode == "tags",
											},
										]}
										placeholder="Tipo de busca"
									/>
								</span>
							</SimpleTooltip>
						</div>
					</div>
				</div>

				<div className="TreeHead display-flex gap-5 padding-15">
					<div className="border border-right-only hidden-empty padding-right-10 margin-right-2">
						<div className="TreeSelectAll display-flex align-items-center">
							<label className="checkbox margin-top--1" id="checkbox-all">
								<Checkbox
									indeterminate={selectedTags.size > 0 && selectedTags.size < allTags.filter(el => el.id).length}
									checked={selectedTags.size === allTags.filter(el => el.id).length}
									onChange={() => {
										if (selectedTags.size < allTags.filter(el => el.id).length) addAllTags();
										else removeAllTags();
									}}
								/>
							</label>
						</div>
					</div>
					<div className="display-flex justify-content-between align-items-start width-100pct">
						<div className="TreeSummary display-flex flex-wrap align-items-center gap-10 padding-left-3">
							{getVehicleTypeCount("truck") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-truck text-size-16 text-black margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("truck")}</span>
								</div>
							)}
							{getVehicleTypeCount("bus") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-bus text-size-16 text-black margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("bus")}</span>
								</div>
							)}
							{getVehicleTypeCount("van") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-van text-size-16 text-black margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("van")}</span>
								</div>
							)}
							{getVehicleTypeCount("trailer") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-trailer text-size-16 text-black margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("trailer")}</span>
								</div>
							)}
							{getVehicleTypeCount("car") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-car text-size-16 text-black margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("car")}</span>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<div
				className="TreeRoot user-select-none overflow-hidden"
				style={{height: `calc(100% - 100px)`}}
			>
				<div style={{ height: "100%", overflowY: "auto" }}>

					{paginatedTags
						.map((tag) => {
							const tagAssets = tagWithAssets?.[tag.id] ?? { assets: [], selectedAssetIds: new Set() };
							if (tagAssets?.assets?.length === 0) return null;

							return <UptimeTagRow
								key={tag.id}
								tagId={tag.id}
								tagName={tag.name}
								isChecked={selectedTags.has(tag.id)}
								assets={tagAssets.assets}
								selectedAssetIds={tagAssets.selectedAssetIds}
								onTagSelect={() => {
									if (selectedTags.has(tag.id)) {
										removeTag(tag.id);
									} else {
										addTag(tag.id);
									}
								}}
								isAssetChecked={(assetId: string) => tagWithAssets?.[tag.id]?.selectedAssetIds.has(assetId) ?? false}
								onAssetSelect={(assetId: string) => {
									if (tagWithAssets?.[tag.id]?.selectedAssetIds.has(assetId)) {
										tagWithAssets?.[tag.id]?.selectedAssetIds.delete(assetId);
									} else {
										tagWithAssets?.[tag.id]?.selectedAssetIds.add(assetId);
									}
									setUpdateSnapshot(prev => prev + 1);
								}}
								searchQuery={searchQuery}
								viewMode={viewMode}
							/>
						})}

					{/* Show "Carregar mais" button only when no search query is active and there are more tags to load */}
					{!searchQuery && paginatedTags.length < filteredTags.length && (
						<div
							className="LoadMore cursor-pointer text-center py-8 text-color-primary"
							onClick={() => setPage(prev => prev + 1)}
						>
							Carregar mais
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default UptimeSidebar;
